/**
 * « Modifier mes informations » v2 (route account/edit ; SPEC VC5) — maquette 20-compte-informations
 * (cartes Identité, Adresse, Préférences de contact ; « Enregistrer » / « Annuler »).
 *
 * Champs envoyés à POST client/updateclient (puis /client/me relu et retour à Mon compte) : FirstName, LastName,
 * AddressLine1, PostalCode, City, MobilePhone, Email (affiché en lecture seule, SPEC VC5, mais envoyé comme en v1),
 * SendPromotionalEmails (case « actualités, événements et offres »).
 *
 * Retour de Daphné du 07/10 : plus de case « informations importantes concernant mes réservations » (trop de clients
 * la décochaient par erreur et ne recevaient plus annulations, changements de coach, rappels). C'est le champ
 * Mindbody SendScheduleEmails (e-mails de planning : confirmations, rappels, annulations, liste d'attente) ; il n'est
 * plus envoyé (lib/profile.ts), et le serveur (ClientService::updateClientInfo) ne touche alors ce champ sur aucune
 * fiche de la région : chaque marque garde sa valeur (revue du 08/10 : renvoyer la valeur du site courant la recopiait
 * sur les autres marques). Les e-mails de compte (SendAccountEmails : reçus, prélèvements) ne sont pas touchés par ce
 * formulaire (posés à l'inscription, comme la case retirée de l'inscription). Pas des cases SMS de la maquette
 * (absentes de la v1).
 *
 * Retours de Daphné du 12/10 : « Modifier mon mot de passe » quitte la page principale de Mon compte pour cette page,
 * en bas, dans une carte « Mot de passe » à part (components/PasswordChange.tsx ; masquée en OAuth).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useFetchMe } from 'hooks/useApiQueries';
import { useEffect, useState } from 'react';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import type { MindbodyUser } from 'types/client';
import { HTTP } from 'utils/http';
import { ApiClientException } from 'utils/apiClientException';
import { E_MAIL } from '../../lib/typo';
import { profileUpdateBody } from './lib/profile';
import { Icon } from './components/Icon';
import { PasswordCard } from './components/PasswordChange';
import { AccountShell, BackLink, Notice } from './components/Ui';

type FormData = Pick<
  MindbodyUser,
  | 'FirstName'
  | 'LastName'
  | 'AddressLine1'
  | 'PostalCode'
  | 'City'
  | 'MobilePhone'
  | 'Email'
  | 'SendPromotionalEmails'
>;

/** = ProfileEdit.tsx getDefaultValues() */
function defaultsFrom(user: MindbodyUser | undefined): Partial<FormData> {
  if (!user) return {};
  const { FirstName, LastName, AddressLine1, PostalCode, City, MobilePhone, Email, SendPromotionalEmails } = user;
  return { FirstName, LastName, AddressLine1, PostalCode, City, MobilePhone, Email, SendPromotionalEmails };
}

export function ProfileEdit() {
  const { data: user, isLoading } = useFetchMe();

  return (
    <AccountShell className="mmv2-acct--page mmv2-acct--narrow">
      <BackLink />
      <h1 className="mmv2-acct-title mmv2-acct-title--ink">Modifier mes informations</h1>
      {isLoading || !user ? (
        <div className="mmv2-acct-skeleton mmv2-acct-skeleton--table" aria-busy="true" aria-label="Chargement de vos informations" />
      ) : (
        <>
          <ProfileEditForm user={user} />
          <PasswordCard />
        </>
      )}
    </AccountShell>
  );
}

function Field({
  id,
  label,
  registration,
  type = 'text',
  autoComplete,
  readOnly,
  required,
  hint,
}: {
  id: string;
  label: string;
  registration: UseFormRegisterReturn;
  type?: string;
  autoComplete?: string;
  readOnly?: boolean;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div className="mmv2-field mmv2-acct-field">
      <label className="mmv2-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={`mmv2-input${readOnly ? ' mmv2-acct-input--readonly' : ''}`}
        type={type}
        autoComplete={autoComplete}
        readOnly={readOnly}
        aria-readonly={readOnly || undefined}
        required={required}
        aria-describedby={hint ? `${id}-hint` : undefined}
        {...registration}
      />
      {hint ? (
        <span id={`${id}-hint`} className="mmv2-tiny mmv2-muted">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

function CheckRow({ id, label, registration }: { id: string; label: string; registration: UseFormRegisterReturn }) {
  return (
    <label className="mmv2-acct-checkrow" htmlFor={id}>
      <input id={id} type="checkbox" className="mmv2-acct-checkrow__input" {...registration} />
      <span className="mmv2-acct-checkrow__box" aria-hidden="true">
        <Icon name="check" size={13} stroke={3} />
      </span>
      <span>{label}</span>
    </label>
  );
}

function ProfileEditForm({ user }: { user: MindbodyUser }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm<FormData>({ defaultValues: defaultsFrom(user) });

  useEffect(() => {
    reset(defaultsFrom(user));
  }, [user, reset]);

  // = ProfileEdit.tsx onSubmit()
  const onSubmit = async (formData: Partial<FormData>) => {
    setError(null);
    try {
      await HTTP.post<{ Client: MindbodyUser }>('/client/updateclient', profileUpdateBody(formData)).send();
      await queryClient.invalidateQueries({ queryKey: ['/client/me'] });
      navigate('/account');
    } catch (e) {
      // La v1 ne disait rien ; la v2 dit ce qui s'est passé. Erreur au format WordPress ({ code, message } : garde-fou
      // du staging, refus REST) : pas de Message, getMessage() vide ; texte de repli (revue r4 : échec silencieux).
      setError(
        e instanceof ApiClientException
          ? e.getMessage() || 'Vos informations n’ont pas pu être enregistrées. Réessayez dans un instant.'
          : 'Vos informations n’ont pas pu être enregistrées. Vérifiez votre connexion, puis réessayez.',
      );
    }
  };

  return (
    <form className="mmv2-acct-profile" onSubmit={handleSubmit(onSubmit)}>
      <fieldset className="mmv2-card mmv2-card--pad mmv2-acct-fieldset">
        <legend className="mmv2-acct-kicker">Identité</legend>
        <div className="mmv2-acct-grid2">
          <Field id="pf-first" label="Prénom" autoComplete="given-name" required registration={register('FirstName')} />
          <Field id="pf-last" label="Nom" autoComplete="family-name" required registration={register('LastName')} />
          <Field
            id="pf-email"
            label="E-mail"
            type="email"
            autoComplete="email"
            readOnly
            hint={`L’${E_MAIL} sert d’identifiant\u00a0: il ne se modifie pas ici.`}
            registration={register('Email')}
          />
          <Field id="pf-phone" label="Téléphone" type="tel" autoComplete="tel" registration={register('MobilePhone')} />
        </div>
      </fieldset>

      <fieldset className="mmv2-card mmv2-card--pad mmv2-acct-fieldset">
        <legend className="mmv2-acct-kicker">Adresse</legend>
        <div className="mmv2-acct-grid2">
          <div className="mmv2-acct-grid2__full">
            <Field id="pf-line1" label="Rue" autoComplete="address-line1" registration={register('AddressLine1')} />
          </div>
          <Field id="pf-zip" label="Code postal" autoComplete="postal-code" registration={register('PostalCode')} />
          <Field id="pf-city" label="Ville" autoComplete="address-level2" registration={register('City')} />
        </div>
      </fieldset>

      <fieldset className="mmv2-card mmv2-card--pad mmv2-acct-fieldset">
        <legend className="mmv2-acct-kicker">Préférences de contact</legend>
        <div className="mmv2-acct-checks">
          <CheckRow
            id="pf-promo"
            label="Recevoir les actualités, événements et offres"
            registration={register('SendPromotionalEmails')}
          />
        </div>
      </fieldset>

      {error ? (
        <div className="mmv2-acct-pagenotice">
          <Notice tone="error" role="alert">
            {error}
          </Notice>
        </div>
      ) : null}

      <div className="mmv2-acct-actions">
        <button type="submit" className="mmv2-btn mmv2-btn--primary" disabled={isSubmitting} aria-busy={isSubmitting}>
          {isSubmitting ? 'Enregistrement…' : 'Enregistrer mes informations'}
        </button>
        <button type="button" className="mmv2-btn mmv2-btn--quiet" onClick={() => navigate('/account')}>
          Annuler
        </button>
      </div>
    </form>
  );
}
